import React from 'react';
import { History } from 'lucide-react';
import { ChatLeftQuoteFill, HourglassSplit } from 'react-bootstrap-icons';
import Accordion from '../../common/Accordion';
import {
  UPDATE_VALIDITY_DAYS,
  formatVersionDate,
  getValidThrough,
  isValidThroughExpired
} from '../../../helpers/serviceVersionHelpers';

const Badge = ({ delivered }) => (
  <span
    className="font-weight-semibold midFont ml-2 px-2"
    style={{
      borderRadius: 10,
      backgroundColor: delivered ? '#E8F5E9' : '#FFF3E0',
      color: delivered ? '#2E7D32' : '#E65100'
    }}
  >
    {delivered ? 'Delivered' : 'In Progress'}
  </span>
);

const UpdateHistory = ({ versions = [], order, orderType }) => {
  if (!versions.length) return null;

  const sorted = [...versions].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
  const updateCount = sorted.filter(v => v.versionNumber > 1).length;

  return (
    <div className="card border p-3 mt-3 shadow-sm">
      <Accordion
        title={
          <span className="d-flex align-items-center font-weight-bold largeFont text-dark">
            <History size={18} className="mr-2 text-secondary" />
            Update History — {orderType}
          </span>
        }
        triggerClassName="hover-none"
        additionalComponent={
          <span className="text-muted midFont mr-2">
            {updateCount} update{updateCount === 1 ? '' : 's'} · Click to expand
          </span>
        }
      >
        {sorted.map((version, idx) => {
          const isOriginal = version.versionNumber === 1;
          const delivered = Boolean(version.deliveredAt);
          const closingDate = version.closingDate;
          const validThrough = getValidThrough(closingDate);
          const isExpired = delivered && isValidThroughExpired(validThrough);
          const authorName = version.createdBy?.name;

          return (
            <div
              key={version._id || version.versionNumber}
              className="d-flex"
              style={{
                paddingBottom: idx === sorted.length - 1 ? 0 : 14,
                marginBottom: idx === sorted.length - 1 ? 0 : 14,
                borderBottom: idx === sorted.length - 1 ? 'none' : '1px solid #F1F3F5'
              }}
            >
              <span
                className="mt-1 mr-2 flex-shrink-0"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: delivered ? '#28a745' : '#f59e0b',
                  marginTop: 6
                }}
              />
              <div className="w-100">
                <span className="font-weight-bold midFont">{version.versionLabel}</span>
                <Badge delivered={delivered} />

                <div className="text-muted small mt-1">
                  {isOriginal ? (
                    <>
                      Ordered {formatVersionDate(order?.createdAt)} · Delivered{' '}
                      {formatVersionDate(version.deliveredAt)} · Closing: {formatVersionDate(closingDate)}
                    </>
                  ) : (
                    <>
                      Requested {formatVersionDate(version.createdAt)}
                      {authorName ? ` by ${authorName}` : ''} · New closing: {formatVersionDate(closingDate)}
                    </>
                  )}
                </div>

                {(version.reason || version.notes) && (
                  <div
                    className="midFont mt-2 p-2"
                    style={{ backgroundColor: '#F8F9FA', borderRadius: 8, color: '#495057' }}
                  >
                    <ChatLeftQuoteFill size={12} className="mr-1 text-muted" />
                    {authorName ? `${authorName}: ` : ''}&quot;
                    {[version.reason, version.notes].filter(Boolean).join('-')}&quot;
                  </div>
                )}

                {delivered && validThrough && (
                  <div className="small mt-1" style={{ color: isExpired ? '#E53E3E' : '#ADB5BD' }}>
                    <HourglassSplit size={12} className="mr-1" />
                    Valid through {validThrough.format('MM/DD/YYYY')} ({UPDATE_VALIDITY_DAYS} days)
                    {isExpired ? ' — Expired' : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Accordion>
    </div>
  );
};

export default UpdateHistory;
